import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { SplitText } from '@/components/generals/split-text'
import { Reveal } from '@/components/generals/reveal'
import { Rule } from '@/components/generals/rule'

import { media } from '@/constants/media'
import { studioMeta } from '@/constants/site'
import { socials } from '@/constants/socials'

/* The reference indents the opening paragraph by 27 characters so the copy
   starts under the middle of the headline instead of at the column edge. */
const INDENT = '\u00a0'.repeat(27)

const COPY_DELAY = 0.6
const COPY_STAGGER = 0.014

/* Glyphs in the first colour run. Handing the count to the second run keeps
   one continuous stagger across the two-tone sentence. */
const LEAD_CHARS = 37

/* The hero was a headline and one line of copy on an empty off-white field.
   It is a composition now: the type holds the left, a plate holds the right
   with the founder and the current availability sitting on it, and the details
   close it off on a ruled foot. */
export const ContactHero: FC = (): ReactNode => {
    const scope = useRef<HTMLElement>(null)
    const heading = useRef<HTMLSpanElement>(null)

    useGsap(() => {
        const node = heading.current

        if (!node) {
            return
        }

        /* The headline is not split: it rises as one solid block out of its own
           mask, which is what gives the page its heavy opening beat. */
        if (prefersReducedMotion()) {
            gsap.set(node, { yPercent: 0 })
            gsap.set('[data-contact-plate]', { clipPath: 'inset(0%)' })

            return
        }

        gsap.fromTo(node,
            { yPercent: 110 },
            {
                yPercent: 0,
                duration: 1.35,
                delay: 0.25,
                ease: 'noma'
            }
        )

        gsap.fromTo('[data-contact-plate]',
            { clipPath: 'inset(0% 0% 100% 0%)' },
            {
                clipPath: 'inset(0% 0% 0% 0%)',
                duration: 1.5,
                delay: 0.5,
                ease: 'noma'
            }
        )

        gsap.fromTo('[data-contact-plate] img',
            { scale: 1.2 },
            {
                scale: 1,
                duration: 1.9,
                delay: 0.5,
                ease: 'noma'
            }
        )
    }, scope, [])

    return (
        <section
            id='contact-hero'
            ref={scope}
            className='relative bg-paper pt-[var(--nav-h)] text-ink'
        >
            <div className='mx-auto flex max-w-[1920px] flex-col gap-10 px-5 pb-12 pt-[70px] lg:gap-14 lg:px-[33px] lg:pb-[80px] lg:pt-[104px]'>
                <div className='grid gap-10 lg:grid-cols-12 lg:items-end lg:gap-8'>
                    <div className='flex flex-col gap-7 lg:col-span-8 lg:gap-9'>
                        <span className='flex items-center gap-3'>
                            <span aria-hidden='true' className='block h-px w-8 bg-brand' />

                            <span className='t-mono text-brand'>
                                (Contact)
                            </span>

                            <span className='t-mono text-ash'>
                                {studioMeta.bases.join(' / ')}
                            </span>
                        </span>

                        <h1
                            className='t-display text-ink'
                            style={{ fontSize: 'clamp(45px, 8vw, 128px)' }}
                        >
                            <span className='-mb-[0.08em] block overflow-hidden pb-[0.08em]'>
                                <span
                                    ref={heading}
                                    className='will-anim block'
                                    style={{ transform: 'translateY(110%)' }}
                                >
                                    start a project.
                                </span>
                            </span>
                        </h1>

                        <p
                            className='t-cond max-w-[46ch] leading-[1.16]'
                            style={{
                                fontSize: 'clamp(17px, 1.5vw, 21px)',
                                textAlign: 'justify'
                            }}
                        >
                            <span aria-hidden='true' className='whitespace-pre'>
                                {INDENT}
                            </span>

                            <SplitText
                                text='Book a 30-minute call or send us a message.'
                                className='inline text-ash'
                                trigger='mount'
                                stagger={COPY_STAGGER}
                                delay={COPY_DELAY}
                            />

                            {' '}

                            <SplitText
                                text='We respond within 24 hours.'
                                className='inline text-ink'
                                trigger='mount'
                                stagger={COPY_STAGGER}
                                delay={COPY_DELAY + LEAD_CHARS * COPY_STAGGER}
                            />
                        </p>

                        <Reveal
                            from='up'
                            distance={24}
                            delay={0.35}
                            stagger={0.08}
                            childSelector='[data-hero-stat]'
                            className='grid grid-cols-2 gap-x-5 gap-y-6 sm:grid-cols-4'
                        >
                            {[
                                { value: '30 min', label: 'Intro call' },
                                { value: '24 hr', label: 'Reply time' },
                                { value: '2', label: 'Slots this month' },
                                { value: 'Free', label: 'No obligation' }
                            ].map((stat) => (
                                <span key={stat.label} data-hero-stat className='hair-t flex flex-col gap-1.5 pt-3.5'>
                                    <span className='t-display nums text-[clamp(19px,1.8vw,26px)] leading-none text-ink'>
                                        {stat.value}
                                    </span>

                                    <span className='t-mono text-ash'>
                                        {stat.label}
                                    </span>
                                </span>
                            ))}
                        </Reveal>
                    </div>

                    <div className='lg:col-span-4'>
                        <div
                            data-contact-plate
                            className='relative aspect-[4/5] w-full overflow-hidden rounded-[var(--radius-frame)] bg-ink-soft'
                        >
                            <img
                                src={media.editorial.catalogs}
                                alt='A review of printed work laid out on the studio table'
                                loading='eager'
                                decoding='async'
                                className='absolute inset-0 size-full object-cover'
                            />

                            <span
                                aria-hidden='true'
                                className='absolute inset-0 bg-[linear-gradient(to_top,rgba(5,5,5,0.82),rgba(5,5,5,0.05)_55%)]'
                            />

                            <div className='absolute inset-x-0 bottom-0 flex items-center gap-3 p-4'>
                                <img
                                    src={media.people.founderAvatar}
                                    alt=''
                                    aria-hidden='true'
                                    loading='eager'
                                    className='size-11 shrink-0 rounded-[var(--radius-chip)] object-cover'
                                />

                                <span className='flex min-w-0 flex-col gap-1'>
                                    <span className='t-cond text-[15px] leading-none text-paper'>
                                        Jin Yuen
                                    </span>

                                    <span className='t-mono text-ash-soft'>
                                        Takes every first call
                                    </span>
                                </span>

                                <span className='ml-auto flex shrink-0 items-center gap-2'>
                                    <span className='relative flex size-2'>
                                        <span className='absolute inset-0 animate-ping rounded-full bg-brand opacity-60' />

                                        <span className='relative block size-2 rounded-full bg-brand' />
                                    </span>

                                    <span className='t-mono text-paper'>
                                        Open
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='flex flex-col gap-8'>
                    <Rule delay={0.35} />

                    <Reveal
                        from='up'
                        distance={20}
                        delay={0.2}
                        stagger={0.1}
                        childSelector='[data-hero-foot]'
                        className='grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5'
                    >
                        <ul
                            data-hero-foot
                            className='flex h-fit list-none items-center gap-4'
                        >
                            {socials.map((social) => (
                                <li key={social.label}>
                                    <a
                                        href={social.href}
                                        target='_blank'
                                        rel='noreferrer'
                                        data-cursor='link'
                                        aria-label={social.label}
                                        className='block text-ink transition-colors duration-300 hover:text-brand'
                                    >
                                        <svg
                                            viewBox='0 0 24 24'
                                            className='size-[18px]'
                                            aria-hidden='true'
                                            focusable='false'
                                        >
                                            <path d={social.path} fill='currentColor' />
                                        </svg>
                                    </a>
                                </li>
                            ))}
                        </ul>

                        <p data-hero-foot className='flex flex-col gap-2'>
                            <span className='t-mono text-ink'>
                                Address
                            </span>

                            <span className='t-mono max-w-[26ch] leading-[1.6] text-ash'>
                                {studioMeta.address}
                            </span>
                        </p>

                        <p
                            data-hero-foot
                            className='flex flex-col gap-2 sm:col-span-2 sm:items-end sm:text-right lg:col-span-2'
                        >
                            <strong className='t-mono text-ink'>
                                Office Hours
                            </strong>

                            <span className='t-mono leading-[1.6] text-ash'>
                                {studioMeta.hours}
                            </span>
                        </p>
                    </Reveal>
                </div>
            </div>
        </section>
    )
}
